import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "../auth/AuthContext"
import {
  createDefaultDesktopState,
  DESKTOP_DEFAULTS,
  normalizeDesktopPrefs,
  setDesktopStorageAccount,
} from "./desktopStorage"
import {
  chooseWorkspace,
  createWorkspace,
  createWorkspaceChannel,
  desktopAccountId,
  desktopDeviceId,
  listWorkspaceSnapshots,
  loadLocalWorkspace,
  normalizeWorkspace,
  saveLocalWorkspace,
  saveWorkspaceSnapshot,
  workspaceFromServer,
} from "./desktopPersistence"
import { normalizeProductivity, PRODUCTIVITY_DEFAULTS } from "./productivityStorage"

const DesktopWorkspaceContext = createContext(null)
const MAX_UNDO = 50
const MAX_WORKSPACE_REQUEST_BYTES = 2 * 1024 * 1024 - 64 * 1024

const requestBytes = (workspace) => new TextEncoder().encode(JSON.stringify({
  base_revision: workspace?.baseRevision ?? 0,
  workspace,
})).byteLength

const draftWithinBudget = (workspace, state, prefs, productivity) => requestBytes({
  ...(workspace || {}), state, prefs, productivity,
}) <= MAX_WORKSPACE_REQUEST_BYTES

const monitorParams = () => {
  const params = new URLSearchParams(window.location.search)
  const explicitMonitorId = params.get("monitor")?.slice(0, 80) || ""
  return {
    monitorId: explicitMonitorId || `primary-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    monitorStorageId: explicitMonitorId || "primary",
    primaryMonitor: !explicitMonitorId,
    requestedDesktopId: params.get("desktop")?.slice(0, 80) || null,
  }
}

export function DesktopWorkspaceProvider({ children }) {
  const { user, authFetch } = useAuth()
  const accountId = desktopAccountId(user)
  const [{ monitorId, monitorStorageId, primaryMonitor, requestedDesktopId }] = useState(monitorParams)
  const [state, setStateInternal] = useState(createDefaultDesktopState)
  const [prefs, setPrefsInternal] = useState(() => ({ ...DESKTOP_DEFAULTS }))
  const [productivity, setProductivityInternal] = useState(() => ({ ...PRODUCTIVITY_DEFAULTS }))
  const [ready, setReady] = useState(false)
  const [syncStatus, setSyncStatus] = useState("loading")
  const [conflict, setConflict] = useState(null)
  const [snapshots, setSnapshots] = useState([])
  const [peerCount, setPeerCount] = useState(0)
  const [adaptiveSlowdown, setAdaptiveSlowdown] = useState(false)
  const workspaceRef = useRef(null)
  const stateRef = useRef(state)
  const prefsRef = useRef(prefs)
  const productivityRef = useRef(productivity)
  const channelRef = useRef(null)
  const syncTimer = useRef(null)
  const syncInFlight = useRef(false)
  const localSaveTimer = useRef(null)
  const undoRef = useRef([])
  const redoRef = useRef([])
  const peersRef = useRef(new Map())
  const accountEpochRef = useRef(0)
  const peerPendingTimer = useRef(null)
  const workspaceReady = ready && !!accountId && workspaceRef.current?.accountId === accountId

  useEffect(() => {
    setDesktopStorageAccount(accountId || "signed-out")
  }, [accountId])

  const refreshSnapshots = useCallback(() => {
    if (!accountId) return Promise.resolve([])
    return listWorkspaceSnapshots(accountId).then((items) => {
      setSnapshots(items)
      return items
    })
  }, [accountId])

  const adoptWorkspace = useCallback((workspace) => {
    const normalizedValue = normalizeWorkspace(workspace, accountId)
    const normalized = normalizedValue ? {
      ...normalizedValue,
      deviceId: desktopDeviceId(),
      peerPending: !!workspace.peerPending,
    } : null
    if (!normalized) return
    workspaceRef.current = normalized
    stateRef.current = normalized.state
    prefsRef.current = normalized.prefs
    productivityRef.current = normalized.productivity
    setStateInternal(normalized.state)
    setPrefsInternal(normalized.prefs)
    setProductivityInternal(normalized.productivity)
    window.dispatchEvent(new CustomEvent("desktop-layout-changed", { detail: normalized.state }))
    window.dispatchEvent(new CustomEvent("desktop-settings-changed", { detail: normalized.prefs }))
    try {
      localStorage.setItem("startup_page", normalized.prefs.autoStart ? "desktop" : (localStorage.getItem("startup_page") === "desktop" ? "dashboard" : localStorage.getItem("startup_page") ?? "dashboard"))
    } catch { /* optional cache */ }
    saveLocalWorkspace(normalized)
  }, [accountId])

  const syncNow = useCallback(async (force = false) => {
    const current = workspaceRef.current
    const syncAccountId = accountId
    const epoch = accountEpochRef.current
    if (!current || current.accountId !== accountId || !ready) return
    if (!prefsRef.current.accountSyncEnabled && !current.forceSync && force !== true) {
      setSyncStatus("local")
      return
    }
    if (!navigator.onLine) {
      setSyncStatus("offline")
      return
    }
    if (syncInFlight.current) return
    const syncToken = Symbol("desktop-sync")
    syncInFlight.current = syncToken
    setSyncStatus("syncing")
    try {
      const candidate = {
        ...current,
        forceSync: false,
        pending: false,
        lastSyncedAt: new Date().toISOString(),
      }
      if (requestBytes(candidate) > MAX_WORKSPACE_REQUEST_BYTES) {
        setSyncStatus("error")
        window.dispatchEvent(new CustomEvent("holos-workspace-budget-exceeded"))
        return
      }
      const response = await authFetch("/api/desktop/workspace/", {
        method: "PATCH",
        body: JSON.stringify({
          base_revision: current.baseRevision,
          workspace: candidate,
        }),
      })
      if (accountEpochRef.current !== epoch || workspaceRef.current?.accountId !== syncAccountId) return
      if (response.status === 409) {
        const data = await response.json()
        if (accountEpochRef.current !== epoch || workspaceRef.current?.accountId !== syncAccountId) return
        const normalizedRemote = normalizeWorkspace(data.workspace, accountId)
        const remoteServerRevision = Number(data.revision) || 0
        const remote = {
          ...normalizedRemote,
          revision: Math.max(normalizedRemote?.revision || 0, remoteServerRevision),
          baseRevision: remoteServerRevision,
          pending: false,
        }
        const nextConflict = { local: current, remote }
        setConflict(nextConflict)
        setSyncStatus("conflict")
        await saveWorkspaceSnapshot(current, "conflict-local")
        await saveWorkspaceSnapshot(remote, "conflict-remote")
        await refreshSnapshots()
        return
      }
      if (!response.ok) throw new Error("sync-write")
      const data = await response.json()
      if (accountEpochRef.current !== epoch || workspaceRef.current?.accountId !== syncAccountId) return
      const serverRevision = Math.max(0, Number(data.revision) || current.baseRevision + 1)
      const latest = workspaceRef.current
      if (latest?.revision !== current.revision) {
        const pending = { ...latest, baseRevision: serverRevision, pending: true }
        workspaceRef.current = pending
        await saveLocalWorkspace(pending)
        setSyncStatus("local-pending")
        clearTimeout(syncTimer.current)
        syncTimer.current = setTimeout(syncNow, 650)
      } else {
        const synced = {
          ...candidate,
          revision: Math.max(candidate.revision, serverRevision),
          baseRevision: serverRevision,
        }
        workspaceRef.current = synced
        await saveLocalWorkspace(synced)
        setSyncStatus(synced.prefs.accountSyncEnabled ? "synced" : "local")
        channelRef.current?.postMessage({ type: "workspace", sender: monitorId, workspace: synced })
        window.dispatchEvent(new CustomEvent("desktop-workspace-synced", { detail: synced.lastSyncedAt }))
      }
    } catch {
      if (accountEpochRef.current === epoch && workspaceRef.current?.accountId === syncAccountId) {
        setSyncStatus(navigator.onLine ? "error" : "offline")
      }
    } finally {
      if (syncInFlight.current === syncToken) syncInFlight.current = null
    }
  }, [accountId, authFetch, monitorId, ready, refreshSnapshots])

  const scheduleSync = useCallback(() => {
    clearTimeout(syncTimer.current)
    syncTimer.current = setTimeout(syncNow, 650)
  }, [syncNow])

  const markDirty = useCallback((nextState, nextPrefs, nextProductivity, reason) => {
    const stored = workspaceRef.current
    const current = stored?.accountId === accountId ? stored : createWorkspace(accountId, nextState, nextPrefs, nextProductivity)
    const next = {
      ...current,
      revision: current.revision + 1,
      updatedAt: new Date().toISOString(),
      pending: true,
      peerPending: false,
      forceSync: !!current.forceSync || (current.prefs.accountSyncEnabled && !nextPrefs.accountSyncEnabled),
      state: nextState,
      prefs: nextPrefs,
      productivity: nextProductivity,
    }
    if (requestBytes(next) > MAX_WORKSPACE_REQUEST_BYTES) {
      setSyncStatus("error")
      window.dispatchEvent(new CustomEvent("holos-workspace-budget-exceeded"))
      return
    }
    workspaceRef.current = next
    setSyncStatus(next.prefs.accountSyncEnabled ? "local-pending" : "local")
    clearTimeout(localSaveTimer.current)
    localSaveTimer.current = setTimeout(() => saveLocalWorkspace(workspaceRef.current), 60)
    channelRef.current?.postMessage({ type: "workspace", sender: monitorId, workspace: next })
    if (next.revision % 25 === 0) {
      saveWorkspaceSnapshot(next, reason || "automatic").then(refreshSnapshots)
    }
    scheduleSync()
  }, [accountId, monitorId, refreshSnapshots, scheduleSync])

  const setState = useCallback((updater, options = {}) => {
    const current = stateRef.current
    const next = typeof updater === "function" ? updater(current) : updater
    if (!next || next === current) return
    if (!draftWithinBudget(workspaceRef.current, next, prefsRef.current, productivityRef.current)) {
      setSyncStatus("error")
      window.dispatchEvent(new CustomEvent("holos-workspace-budget-exceeded"))
      return
    }
    if (options.history !== false) {
      undoRef.current = [...undoRef.current.slice(-(MAX_UNDO - 1)), current]
      redoRef.current = []
    }
    stateRef.current = next
    setStateInternal(next)
    window.dispatchEvent(new CustomEvent("desktop-layout-changed", { detail: next }))
    markDirty(next, prefsRef.current, productivityRef.current, options.reason ?? "layout-change")
  }, [markDirty])

  const setPrefs = useCallback((updater) => {
    const current = prefsRef.current
    const next = normalizeDesktopPrefs(typeof updater === "function" ? updater(current) : updater)
    if (!draftWithinBudget(workspaceRef.current, stateRef.current, next, productivityRef.current)) {
      setSyncStatus("error")
      window.dispatchEvent(new CustomEvent("holos-workspace-budget-exceeded"))
      return
    }
    prefsRef.current = next
    setPrefsInternal(next)
    try {
      localStorage.setItem("startup_page", next.autoStart ? "desktop" : (localStorage.getItem("startup_page") === "desktop" ? "dashboard" : localStorage.getItem("startup_page") ?? "dashboard"))
    } catch { /* optional cache */ }
    markDirty(stateRef.current, next, productivityRef.current, "settings-change")
    window.dispatchEvent(new CustomEvent("desktop-settings-changed", { detail: next }))
  }, [markDirty])

  const setProductivity = useCallback((updater) => {
    const current = productivityRef.current
    const next = normalizeProductivity(typeof updater === "function" ? updater(current) : updater)
    if (!draftWithinBudget(workspaceRef.current, stateRef.current, prefsRef.current, next)) {
      setSyncStatus("error")
      window.dispatchEvent(new CustomEvent("holos-workspace-budget-exceeded"))
      return
    }
    productivityRef.current = next
    setProductivityInternal(next)
    markDirty(stateRef.current, prefsRef.current, next, "productivity-change")
  }, [markDirty])

  useEffect(() => {
    const receiveSettings = (event) => {
      const incoming = normalizeDesktopPrefs(event.detail)
      if (JSON.stringify(incoming) === JSON.stringify(prefsRef.current)) return
      setPrefs(incoming)
    }
    window.addEventListener("desktop-settings-changed", receiveSettings)
    return () => window.removeEventListener("desktop-settings-changed", receiveSettings)
  }, [setPrefs])

  const undo = useCallback(() => {
    const previous = undoRef.current.pop()
    if (!previous) return false
    redoRef.current.push(stateRef.current)
    stateRef.current = previous
    setStateInternal(previous)
    markDirty(previous, prefsRef.current, productivityRef.current, "undo")
    return true
  }, [markDirty])

  const redo = useCallback(() => {
    const next = redoRef.current.pop()
    if (!next) return false
    undoRef.current.push(stateRef.current)
    stateRef.current = next
    setStateInternal(next)
    markDirty(next, prefsRef.current, productivityRef.current, "redo")
    return true
  }, [markDirty])

  const createSnapshot = useCallback(async (reason = "manual") => {
    if (!workspaceRef.current) return
    await saveWorkspaceSnapshot(workspaceRef.current, reason)
    await refreshSnapshots()
  }, [refreshSnapshots])

  const restoreSnapshot = useCallback((snapshot) => {
    if (!snapshot) return
    if (workspaceRef.current) saveWorkspaceSnapshot(workspaceRef.current, "before-restore")
    setState(snapshot.state, { reason: "snapshot-restore" })
    setPrefs(snapshot.prefs)
    setProductivity(snapshot.productivity)
  }, [setPrefs, setProductivity, setState])

  const resolveConflict = useCallback((choice) => {
    if (!conflict) return
    if (choice === "remote") saveWorkspaceSnapshot(conflict.local, "conflict-local")
    const selected = choice === "remote" ? conflict.remote : conflict.local
    const next = {
      ...selected,
      deviceId: workspaceRef.current?.deviceId ?? selected.deviceId,
      revision: Math.max(conflict.local.revision, conflict.remote.revision) + 1,
      baseRevision: conflict.remote.baseRevision,
      pending: choice !== "remote",
      updatedAt: new Date().toISOString(),
    }
    setConflict(null)
    adoptWorkspace(next)
    if (choice === "remote") setSyncStatus("synced")
    else {
      setSyncStatus("local-pending")
      scheduleSync()
    }
  }, [adoptWorkspace, conflict, scheduleSync])

  const openMonitor = useCallback((currentDesktopId = stateRef.current.activeDesktopId) => {
    const desktops = stateRef.current.desktops
    const currentIndex = Math.max(0, desktops.findIndex((desktop) => desktop.id === currentDesktopId))
    const target = desktops[(currentIndex + 1) % desktops.length]
    const id = `monitor-${Date.now().toString(36)}`
    const url = new URL("/desktop", window.location.origin)
    url.searchParams.set("monitor", id)
    url.searchParams.set("desktop", target.id)
    const width = Math.max(900, window.screen?.availWidth || 1280)
    const height = Math.max(640, window.screen?.availHeight || 800)
    const opened = window.open(url.toString(), `holos-${id}`, `popup=yes,width=${width},height=${height},left=${window.screenX + 48},top=${window.screenY + 48}`)
    if (!opened) return false
    opened.focus()
    return true
  }, [])

  useEffect(() => {
    const epoch = ++accountEpochRef.current
    clearTimeout(syncTimer.current)
    clearTimeout(localSaveTimer.current)
    clearTimeout(peerPendingTimer.current)
    syncInFlight.current = null
    undoRef.current = []
    redoRef.current = []
    peersRef.current.clear()
    setPeerCount(0)
    if (!accountId) {
      setReady(false)
      setSyncStatus("signed-out")
      workspaceRef.current = null
      const cleanState = createDefaultDesktopState()
      const cleanPrefs = { ...DESKTOP_DEFAULTS }
      const cleanProductivity = { ...PRODUCTIVITY_DEFAULTS }
      stateRef.current = cleanState
      prefsRef.current = cleanPrefs
      productivityRef.current = cleanProductivity
      setStateInternal(cleanState)
      setPrefsInternal(cleanPrefs)
      setProductivityInternal(cleanProductivity)
      return undefined
    }
    let active = true
    setReady(false)
    setSyncStatus("loading")
    setConflict(null)
    const cleanState = createDefaultDesktopState()
    const cleanPrefs = { ...DESKTOP_DEFAULTS }
    const cleanProductivity = { ...PRODUCTIVITY_DEFAULTS }
    stateRef.current = cleanState
    prefsRef.current = cleanPrefs
    productivityRef.current = cleanProductivity
    setStateInternal(cleanState)
    setPrefsInternal(cleanPrefs)
    setProductivityInternal(cleanProductivity)
    const fallback = createWorkspace(accountId)
    Promise.all([
      loadLocalWorkspace(accountId),
      authFetch("/api/desktop/workspace/")
        .then(async (response) => {
          if (!response.ok) throw new Error("workspace-unavailable")
          const data = await response.json()
          if (data.workspace && Object.keys(data.workspace).length) {
            const normalizedRemote = normalizeWorkspace(data.workspace, accountId)
            const remoteServerRevision = Math.max(0, Number(data.revision) || 0)
            return {
              ...normalizedRemote,
              revision: Math.max(normalizedRemote?.revision || 0, remoteServerRevision),
              baseRevision: remoteServerRevision,
              pending: false,
            }
          }
          const legacyResponse = await authFetch("/api/auth/prefs/")
          const legacyData = legacyResponse.ok ? await legacyResponse.json() : { prefs: {} }
          const legacy = workspaceFromServer(legacyData.prefs, accountId)
          return legacy ? { ...legacy, baseRevision: 0, pending: true } : null
        })
        .catch(async () => {
          try {
            const response = await authFetch("/api/auth/prefs/")
            const data = response.ok ? await response.json() : { prefs: {} }
            const legacy = workspaceFromServer(data.prefs, accountId)
            return legacy ? { ...legacy, baseRevision: 0, pending: true } : null
          } catch {
            return null
          }
        }),
    ]).then(async ([local, remote]) => {
      if (!active || accountEpochRef.current !== epoch) return
      const selected = chooseWorkspace(local, remote, fallback)
      adoptWorkspace(selected.workspace)
      setConflict(selected.conflict)
      setSyncStatus(selected.conflict ? "conflict" : selected.workspace.pending ? "local-pending" : remote ? "synced" : "local")
      if (selected.conflict) {
        await saveWorkspaceSnapshot(selected.conflict.local, "conflict-local")
        await saveWorkspaceSnapshot(selected.conflict.remote, "conflict-remote")
      }
      await refreshSnapshots()
      if (active && accountEpochRef.current === epoch) setReady(true)
    })
    return () => { active = false }
  }, [accountId, adoptWorkspace, authFetch, refreshSnapshots])

  useEffect(() => {
    if (!ready || !accountId) return undefined
    const channel = createWorkspaceChannel(accountId)
    channelRef.current = channel
    if (!channel) return undefined
    const announce = () => channel.postMessage({ type: "presence", sender: monitorId, at: Date.now() })
    const prunePeers = () => {
      const cutoff = Date.now() - 12000
      peersRef.current.forEach((seen, id) => { if (seen < cutoff) peersRef.current.delete(id) })
      setPeerCount(peersRef.current.size)
      announce()
    }
    channel.onmessage = (event) => {
      const message = event.data
      if (!message || message.sender === monitorId) return
      if (message.type === "presence") {
        peersRef.current.set(message.sender, Number(message.at) || Date.now())
        setPeerCount(peersRef.current.size)
        return
      }
      if (message.type !== "workspace" || !prefsRef.current.monitorSyncEnabled) return
      const incoming = normalizeWorkspace(message.workspace, accountId)
      const current = workspaceRef.current
      if (!incoming || !current || incoming.revision < current.revision) return
      if (incoming.revision === current.revision) {
        if (current.peerPending && !incoming.pending) {
          clearTimeout(peerPendingTimer.current)
          adoptWorkspace({ ...incoming, peerPending: false })
          setSyncStatus("synced")
        }
        return
      }
      if (current.pending && !current.peerPending) {
        setConflict({ local: current, remote: incoming })
        setSyncStatus("conflict")
        saveWorkspaceSnapshot(current, "conflict-local")
        saveWorkspaceSnapshot(incoming, "conflict-remote").then(refreshSnapshots)
        return
      }
      if (incoming.pending) {
        adoptWorkspace({ ...incoming, pending: false, peerPending: true })
        setSyncStatus("syncing")
        clearTimeout(peerPendingTimer.current)
        peerPendingTimer.current = setTimeout(() => {
          const stalled = workspaceRef.current
          if (!stalled?.peerPending || stalled.accountId !== accountId) return
          markDirty(stalled.state, stalled.prefs, stalled.productivity, "monitor-takeover")
        }, 4000)
      } else {
        clearTimeout(peerPendingTimer.current)
        adoptWorkspace({ ...incoming, peerPending: false })
        setSyncStatus("synced")
      }
    }
    announce()
    const heartbeat = setInterval(prunePeers, 4000)
    return () => {
      clearInterval(heartbeat)
      clearTimeout(peerPendingTimer.current)
      channel.close()
      channelRef.current = null
      peersRef.current.clear()
      setPeerCount(0)
    }
  }, [accountId, adoptWorkspace, markDirty, monitorId, ready])

  useEffect(() => {
    const online = () => { if (workspaceRef.current?.pending) syncNow() }
    window.addEventListener("online", online)
    return () => window.removeEventListener("online", online)
  }, [syncNow])

  useEffect(() => {
    if (!workspaceReady) return undefined
    if (workspaceRef.current?.pending && !conflict) scheduleSync()
    const automaticSnapshot = setInterval(() => {
      if (workspaceRef.current) saveWorkspaceSnapshot(workspaceRef.current, "automatic").then(refreshSnapshots)
    }, 5 * 60 * 1000)
    return () => clearInterval(automaticSnapshot)
  }, [conflict, refreshSnapshots, scheduleSync, workspaceReady])

  useEffect(() => {
    if (!prefs.adaptivePerformance || typeof PerformanceObserver === "undefined") {
      setAdaptiveSlowdown(false)
      return undefined
    }
    let longTasks = 0
    let recoveryTimer = null
    const observer = new PerformanceObserver((list) => {
      longTasks += list.getEntries().filter((entry) => entry.duration >= 80).length
      if (longTasks < 3) return
      setAdaptiveSlowdown(true)
      clearTimeout(recoveryTimer)
      recoveryTimer = setTimeout(() => {
        longTasks = 0
        setAdaptiveSlowdown(false)
      }, 20000)
    })
    try { observer.observe({ type: "longtask", buffered: true }) } catch { return undefined }
    return () => {
      observer.disconnect()
      clearTimeout(recoveryTimer)
    }
  }, [prefs.adaptivePerformance])

  useEffect(() => {
    if (!prefs.adaptivePerformance) return undefined
    const visibility = () => setAdaptiveSlowdown(document.hidden)
    document.addEventListener("visibilitychange", visibility)
    return () => document.removeEventListener("visibilitychange", visibility)
  }, [prefs.adaptivePerformance])

  useEffect(() => () => {
    clearTimeout(syncTimer.current)
    clearTimeout(localSaveTimer.current)
    clearTimeout(peerPendingTimer.current)
  }, [])

  const value = useMemo(() => ({
    state,
    setState,
    prefs,
    setPrefs,
    productivity,
    setProductivity,
    ready: workspaceReady,
    syncStatus,
    syncNow,
    conflict,
    resolveConflict,
    snapshots,
    createSnapshot,
    restoreSnapshot,
    refreshSnapshots,
    undo,
    redo,
    canUndo: undoRef.current.length > 0,
    canRedo: redoRef.current.length > 0,
    accountId,
    monitorId,
    monitorStorageId,
    primaryMonitor,
    requestedDesktopId,
    peerCount,
    openMonitor,
    adaptiveSlowdown,
    workspace: workspaceRef.current,
  }), [
    accountId, adaptiveSlowdown, conflict, createSnapshot, monitorId, monitorStorageId, openMonitor, peerCount, prefs, primaryMonitor, productivity,
    redo, refreshSnapshots, requestedDesktopId, resolveConflict, restoreSnapshot, setPrefs,
    setProductivity, setState, snapshots, state, syncNow, syncStatus, undo,
    workspaceReady,
  ])

  return <DesktopWorkspaceContext.Provider value={value}>{children}</DesktopWorkspaceContext.Provider>
}

export function useDesktopWorkspace() {
  const value = useContext(DesktopWorkspaceContext)
  if (!value) throw new Error("useDesktopWorkspace requires DesktopWorkspaceProvider")
  return value
}
